/**
 * Hook that loads the contract extraction data for a player and document type.
 *
 * Wraps the `getContractExtraction` GraphQL query with Apollo's
 * `cache-and-network` fetch policy so the drawer can render stale data
 * immediately and then refresh once the server responds. The document
 * is defined inline via `gql` rather than imported from
 * `generated/graphql.ts` so the hook can ship before the codegen step
 * runs against the updated schema.
 * @module features/contracts/hooks/useContractExtraction
 */

import { gql, useQuery } from "@apollo/client";
import { useMemo } from "react";

import type { ContractExtraction } from "../types";

/** GraphQL document for the GetContractExtraction query. */
export const GET_CONTRACT_EXTRACTION_QUERY = gql`
  query GetContractExtraction(
    $playerId: ID!
    $documentType: DocumentType!
    $archived: Boolean
  ) {
    getContractExtraction(
      playerId: $playerId
      documentType: $documentType
      archived: $archived
    ) {
      id
      playerId
      documentType
      status
      extractedData
      savedAt
      archivedAt
      # SE-4881: literal YYYY-MM-DD string (not a Date). Pending codegen
      # reconciliation after the backend deploy adds this field to the
      # live schema; selecting it here populates extraction.endDate for
      # the dedicated ContractEndDateSection.
      endDate
    }
  }
`;

/** Shape of the GetContractExtraction query response. */
interface GetContractExtractionResponse {
  readonly getContractExtraction: ContractExtraction | null;
}

/** Shape of the GetContractExtraction query variables. */
interface GetContractExtractionVariables {
  readonly playerId: string;
  readonly documentType: string;
  readonly archived?: boolean;
}

/** Public API returned by `useContractExtraction`. */
export interface UseContractExtractionResult {
  /** Extraction data returned by the server, or null when none exists. */
  readonly extraction: ContractExtraction | null;
  /** Whether the initial load is in flight (no cached data yet). */
  readonly loading: boolean;
  /**
   * Manually re-runs the query against the network. Used after
   * delete mutations to keep extraction state in sync.
   */
  readonly refetch: () => Promise<void>;
  /** Starts polling the extraction query at the given interval (ms). */
  readonly startPolling: (intervalMs: number) => void;
  /** Stops any active polling started by `startPolling`. */
  readonly stopPolling: () => void;
}

/**
 * Loads the contract extraction data for a player and document type.
 *
 * SE-4546: pass `archived: true` to read an archived row (the
 * default-`false` server arg only returns active rows). The archived
 * variant is what powers `ArchivedDrawerBody` so the read-only view
 * can render extraction data + the Restore affordance.
 * @param playerId - Player UUID the drawer is currently open for, or null when the drawer is closed
 * @param documentType - Document type to query extraction for
 * @param options - Optional flags. `archived: true` returns the
 *   archived row instead of the active one (SE-4546 / SE-4349 AC-4).
 * @param options.archived - When true, query for the archived row;
 *   when false / omitted, query for the active row.
 * @returns Extraction data + loading state + refetch helper
 */
export function useContractExtraction(
  playerId: string | null,
  documentType: string,
  options?: { readonly archived?: boolean }
): UseContractExtractionResult {
  const archived = options?.archived ?? false;
  const { data, loading, refetch, startPolling, stopPolling } = useQuery<
    GetContractExtractionResponse,
    GetContractExtractionVariables
  >(GET_CONTRACT_EXTRACTION_QUERY, {
    variables: playerId ? { playerId, documentType, archived } : undefined,
    skip: !playerId,
    fetchPolicy: "cache-and-network",
    notifyOnNetworkStatusChange: true,
  });

  const extraction = useMemo<ContractExtraction | null>(
    () => data?.getContractExtraction ?? null,
    [data]
  );

  const refetchWrapped = useMemo(
    () => async (): Promise<void> => {
      await refetch();
    },
    [refetch]
  );

  return {
    extraction,
    loading,
    refetch: refetchWrapped,
    startPolling,
    stopPolling,
  };
}
